// station scenes on the same canvas the ambient patterns use: every station has its own small moving backdrop.
// one fixed pool of at most 48 bits (no allocation per frame), ~30fps, device pixel ratio capped, cross faded when
// the station changes. nothing runs while the tab is hidden, calm motion or sparkle effects are off, reduced motion is
// on, "station worlds" is off, or the person picked a pattern that already animates this canvas (their pick wins)

import { reducedMotion } from "./motion_pref";

type Scene = "home" | "plan" | "studio" | "practice" | "money" | "tools" | "core";

const SCENES: Scene[] = ["home", "plan", "studio", "practice", "money", "tools", "core"];
const MAX = 48;
const FRAME_MS = 1000 / 30;
const DIGITS = "0123456789$%+";
const CODE = "01<>/{}[]=#$";

// per bit: a position, a rate, a phase and a size. tools reuses x and y as its launch velocity
const bx = new Float32Array(MAX);
const by = new Float32Array(MAX);
const bv = new Float32Array(MAX);
const bp = new Float32Array(MAX);
const br = new Float32Array(MAX);

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let width = 1;
let height = 1;
let shown: Scene | null = null;
let target: Scene | null = null;
let fade = 0;
let tint = "#888";
let ink = "#fff";
let frame = 0;
let last = 0;
let clock = 0;
// set by ambient.ts while a pattern that draws on this canvas is running
let patternOn = false;

const rand = Math.random;

function seed(scene: Scene): void {
  for (let i = 0; i < MAX; i++) {
    bx[i] = rand() * width;
    by[i] = rand() * height;
    bv[i] = 0.3 + rand() * 0.9;
    bp[i] = rand() * 6.283;
    br[i] = rand();
    if (scene === "core") bx[i] = Math.floor((rand() * width) / 14) * 14;
    if (scene === "plan") by[i] = Math.round(by[i] / 44) * 44;
    if (scene === "tools") respawnSpark(i, true);
  }
}

function respawnSpark(i: number, anywhere: boolean): void {
  bx[i] = -60 - rand() * 280;
  by[i] = -90 - rand() * 220;
  bv[i] = 0.55 + rand() * 0.8;
  bp[i] = anywhere ? rand() : 0;
}

function size(): void {
  if (!canvas) return;
  const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
  width = canvas.clientWidth || 1;
  height = canvas.clientHeight || 1;
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);
  if (shown) seed(shown);
}

function draw(scene: Scene, dt: number): void {
  const g = ctx!;
  const a = fade;
  g.clearRect(0, 0, width, height);
  g.fillStyle = tint;
  g.strokeStyle = tint;
  if (scene === "home") {
    const n = 40;
    for (let i = 0; i < n; i++) {
      bp[i] += dt * bv[i];
      bx[i] += dt * 3 * bv[i];
      if (bx[i] > width) bx[i] = 0;
      g.globalAlpha = a * (0.12 + 0.4 * (0.5 + 0.5 * Math.sin(bp[i])));
      g.fillStyle = br[i] > 0.7 ? tint : ink;
      const s = 0.8 + br[i] * 1.6;
      g.fillRect(bx[i], by[i], s, s);
    }
  } else if (scene === "plan") {
    const gap = 44;
    const off = (clock * 14) % gap;
    g.globalAlpha = a * 0.13;
    g.lineWidth = 1;
    g.beginPath();
    for (let x = -gap + off; x < width; x += gap) {
      g.moveTo(x, 0);
      g.lineTo(x, height);
    }
    for (let y = -gap + off; y < height; y += gap) {
      g.moveTo(0, y);
      g.lineTo(width, y);
    }
    g.stroke();
    // a few pings travelling along the lines
    g.globalAlpha = a * 0.5;
    for (let i = 0; i < 8; i++) {
      bx[i] += dt * 60 * bv[i];
      if (bx[i] > width) bx[i] = 0;
      g.fillRect(bx[i], ((by[i] + off) % height) - 1, 6, 2);
    }
  } else if (scene === "studio") {
    for (let i = 0; i < 26; i++) {
      bp[i] += dt * 0.6 * bv[i];
      bx[i] += Math.cos(bp[i]) * 8 * dt;
      by[i] += Math.sin(bp[i] * 0.8) * 7 * dt - 2 * dt;
      if (by[i] < -20) by[i] = height + 20;
      if (bx[i] < -20) bx[i] = width + 20;
      if (bx[i] > width + 20) bx[i] = -20;
      g.globalAlpha = a * (0.05 + 0.1 * (0.5 + 0.5 * Math.sin(bp[i] * 1.7)));
      g.beginPath();
      g.arc(bx[i], by[i], 5 + br[i] * 16, 0, 6.283);
      g.fill();
    }
  } else if (scene === "practice") {
    const cx = width * 0.78;
    const cy = height * 0.28;
    const reach = Math.max(width, height) * 0.75;
    g.lineWidth = 1.5;
    for (let i = 0; i < 4; i++) {
      const p = (clock * 0.22 + i / 4) % 1;
      g.globalAlpha = a * (1 - p) * 0.3;
      g.beginPath();
      g.arc(cx, cy, 8 + p * reach, 0, 6.283);
      g.stroke();
    }
    g.globalAlpha = a * (0.25 + 0.2 * Math.sin(clock * 3));
    g.beginPath();
    g.arc(cx, cy, 3, 0, 6.283);
    g.fill();
  } else if (scene === "money") {
    g.font = "13px ui-monospace, Menlo, Consolas, monospace";
    g.textBaseline = "top";
    for (let i = 0; i < 26; i++) {
      by[i] -= dt * 28 * bv[i];
      if (by[i] < -16) {
        by[i] = height + 8;
        br[i] = rand();
      }
      g.globalAlpha = a * (0.1 + 0.22 * (0.5 + 0.5 * Math.sin(bp[i] + clock * 1.4)));
      g.fillText(DIGITS[Math.floor(br[i] * DIGITS.length) % DIGITS.length], bx[i], by[i]);
    }
  } else if (scene === "tools") {
    // sparks thrown off a wheel at the bottom right
    const ox = width * 0.9;
    const oy = height * 0.88;
    g.lineWidth = 1.4;
    g.beginPath();
    for (let i = 0; i < 40; i++) {
      bp[i] += dt * bv[i] * 0.8;
      if (bp[i] >= 1) respawnSpark(i, false);
      const p = bp[i];
      const x = ox + bx[i] * p * 2.2;
      const y = oy + by[i] * p * 1.4 + 520 * p * p;
      g.moveTo(x, y);
      g.lineTo(x - bx[i] * 0.025, y - by[i] * 0.02 - 3);
    }
    g.globalAlpha = a * 0.55;
    g.stroke();
  } else {
    g.font = "13px ui-monospace, Menlo, Consolas, monospace";
    g.textBaseline = "top";
    for (let i = 0; i < 22; i++) {
      by[i] += dt * 60 * (0.5 + bv[i]);
      if (by[i] > height + 70) {
        by[i] = -20;
        bx[i] = Math.floor((rand() * width) / 14) * 14;
      }
      const head = Math.floor(by[i] / 14) + Math.floor(bp[i] * 10);
      for (let t = 0; t < 4; t++) {
        g.globalAlpha = a * (t === 0 ? 0.5 : 0.24 * (1 - t / 4));
        g.fillStyle = t === 0 ? ink : tint;
        g.fillText(CODE[(head + t * 5) % CODE.length], bx[i], by[i] - t * 14);
      }
    }
  }
  g.globalAlpha = 1;
}

function wanted(): boolean {
  const root = document.documentElement;
  return !patternOn && !document.hidden && root.dataset.worlds !== "off" && !root.classList.contains("nojuice") && !reducedMotion();
}

function readColors(): void {
  const css = getComputedStyle(document.body);
  tint = css.getPropertyValue("--station").trim() || tint;
  ink = getComputedStyle(document.documentElement).getPropertyValue("--ink").trim() || ink;
}

function tick(now: number): void {
  frame = requestAnimationFrame(tick);
  if (now - last < FRAME_MS) return;
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  clock += dt;
  if (shown !== target) {
    // fade the old scene out, swap, fade the new one in
    fade -= dt / 0.22;
    if (fade <= 0) {
      fade = 0;
      shown = target;
      if (shown) seed(shown);
    }
  } else if (fade < 1) fade = Math.min(1, fade + dt / 0.5);
  if (!shown) {
    stop(true);
    return;
  }
  draw(shown, dt);
}

function stop(clear: boolean): void {
  cancelAnimationFrame(frame);
  frame = 0;
  if (clear && !patternOn && canvas) ctx?.clearRect(0, 0, canvas.width, canvas.height);
}

function sync(): void {
  const station = document.body.dataset.station as Scene | undefined;
  target = station && SCENES.includes(station) ? station : null;
  if (!canvas || !ctx || !wanted() || (!target && !shown)) {
    stop(true);
    if (!wanted()) {
      shown = null;
      fade = 0;
    }
    return;
  }
  readColors();
  if (frame) return;
  if (Math.abs(canvas.clientWidth - width) > 1 || Math.abs(canvas.clientHeight - height) > 1) size();
  last = performance.now();
  frame = requestAnimationFrame(tick);
}

// ambient.ts tells us whether a pattern owns the canvas
export function sceneOwner(patternRunning: boolean): void {
  patternOn = patternRunning;
  if (!canvas) return;
  // the pattern left the canvas at its own pixel ratio, take it back at ours
  if (!patternRunning) size();
  sync();
}

export function startScenes(): void {
  canvas = document.querySelector<HTMLCanvasElement>("canvas.ambient");
  ctx = canvas?.getContext("2d") || null;
  if (!canvas || !ctx) return;
  const root = document.documentElement;
  new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ["class", "data-worlds", "data-theme", "data-tone"] });
  new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ["data-station"] });
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("resize", () => {
    if (patternOn) return;
    size();
    sync();
  });
  matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", sync);
  size();
  sync();
}
